import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight, Star } from "lucide-react";
import { useMontures } from "@/context/MonturesContext";

export default function CoupDeCoeur() {
    const { coupDeCoeur } = useMontures();
    if (!coupDeCoeur) return null;
    const m = coupDeCoeur;

    return (
        <section
            id="coup-de-coeur"
            className="relative pt-24 md:pt-28 pb-24 md:pb-32 bg-lpl-bg-alt overflow-hidden"
            data-testid="coup-de-coeur-section"
        >
            {/* Decorative shapes */}
            <div
                aria-hidden
                className="pointer-events-none absolute -top-20 -left-20 w-[420px] h-[420px] rounded-full bg-lpl-lavender/30 hidden md:block"
            />

            <div className="relative max-w-7xl mx-auto px-5 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-16 lg:gap-12 items-center">
                {/* LEFT: Polaroid */}
                <motion.div
                    initial={{ opacity: 0, x: -24, rotate: -6 }}
                    animate={{ opacity: 1, x: 0, rotate: -3 }}
                    transition={{ duration: 0.8 }}
                    className="lg:col-span-6 relative flex justify-center"
                >
                    <div className="relative w-full max-w-md">
                        {/* Color block offset behind */}
                        <div
                            aria-hidden
                            className="absolute -bottom-6 -right-6 w-full h-full"
                            style={{ background: "#3D6E5B" }}
                        />
                        {/* Yellow accent square */}
                        <div
                            aria-hidden
                            className="absolute -top-6 -left-6 w-16 h-16 rotate-[18deg] bg-lpl-yellow -z-10"
                        />

                        {/* Polaroid frame */}
                        <div className="relative bg-[#fffdf7] p-4 pb-16 shadow-2xl">
                            {/* Tape on top */}
                            <div
                                aria-hidden
                                className="absolute -top-3 left-1/2 -translate-x-1/2 w-28 h-6 bg-lpl-yellow/70 -rotate-[5deg] shadow-sm"
                            />

                            {/* Coup de cœur sticker */}
                            <div
                                className="absolute -top-3 -right-16 rotate-[10deg] z-20"
                                data-testid="coup-de-coeur-sticker"
                            >
                                <div
                                    className="flex items-center gap-1.5 px-5 py-2.5 rounded-full text-[11px] uppercase tracking-[0.18em] font-bold shadow-xl whitespace-nowrap"
                                    style={{
                                        background: "#DD7259",
                                        color: "#FAF6EF",
                                    }}
                                >
                                    <Star size={13} fill="currentColor" />
                                    Le coup de cœur
                                </div>
                            </div>

                            {m.image ? (
                                <img
                                    src={m.image}
                                    alt={`${m.marque} ${m.modele}`}
                                    className="block w-full aspect-[4/5] object-cover"
                                    data-testid="coup-de-coeur-image"
                                />
                            ) : (
                                <div
                                    className="flex w-full aspect-[4/5] items-center justify-center"
                                    style={{ background: m.bg, color: m.fg }}
                                    data-testid="coup-de-coeur-image-placeholder"
                                >
                                    <span className="font-display font-bold text-6xl tracking-tight">
                                        {m.marque[0]}
                                        {m.modele[0]}
                                    </span>
                                </div>
                            )}

                            <p className="absolute left-0 right-0 bottom-5 text-center font-serif italic text-2xl text-lpl-text">
                                {m.marque} &middot; <span className="font-normal">{m.modele}</span>
                            </p>
                        </div>
                    </div>
                </motion.div>

                {/* RIGHT: infos */}
                <motion.div
                    initial={{ opacity: 0, y: 24 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.15 }}
                    className="lg:col-span-6"
                >
                    <p className="font-serif italic text-2xl md:text-3xl text-lpl-terracotta mb-4">
                        Le coup de cœur de Sylvie
                    </p>

                    <h2
                        className="font-display font-semibold text-5xl md:text-7xl text-lpl-text leading-[0.9] tracking-tight mb-3"
                        data-testid="coup-de-coeur-title"
                    >
                        {m.marque}
                    </h2>
                    <p className="font-serif italic text-3xl md:text-5xl text-lpl-primary-deep leading-none mb-8">
                        {m.modele}
                    </p>

                    <p className="text-lg md:text-xl text-lpl-text-muted leading-relaxed mb-8 max-w-lg">
                        {m.description}
                    </p>

                    <div
                        className="relative p-7 md:p-8 mb-10 max-w-lg"
                        style={{ background: "#ECB6FF", color: "#0F2A20" }}
                    >
                        <span
                            aria-hidden
                            className="absolute -top-4 left-7 font-serif italic text-7xl leading-none"
                            style={{ color: "#DD7259" }}
                        >
                            «
                        </span>
                        <p className="font-serif italic text-xl md:text-2xl leading-snug pt-2">
                            {m.coupDeCoeurNote}
                        </p>
                        <footer className="mt-5 flex items-center gap-3">
                            <span className="h-px flex-1 bg-current opacity-40" />
                            <span className="text-xs uppercase tracking-[0.2em] font-bold">
                                Sylvie
                            </span>
                        </footer>
                    </div>

                    <dl className="flex flex-wrap gap-x-10 gap-y-6 mb-10">
                        <div className="border-l-2 border-lpl-primary pl-4">
                            <dt className="text-xs uppercase tracking-[0.18em] text-lpl-text-muted font-semibold mb-1">
                                Matériau
                            </dt>
                            <dd className="font-display font-semibold text-lg text-lpl-text">
                                {m.materiau}
                            </dd>
                        </div>
                        <div className="border-l-2 border-lpl-terracotta pl-4">
                            <dt className="text-xs uppercase tracking-[0.18em] text-lpl-text-muted font-semibold mb-1">
                                Couleur
                            </dt>
                            <dd className="font-display font-semibold text-lg text-lpl-text">
                                {m.couleur}
                            </dd>
                        </div>
                        <div className="border-l-2 border-lpl-yellow pl-4">
                            <dt className="text-xs uppercase tracking-[0.18em] text-lpl-text-muted font-semibold mb-1">
                                Tarif
                            </dt>
                            <dd className="font-display font-semibold text-lg text-lpl-text">
                                {m.prix}
                            </dd>
                        </div>
                    </dl>

                    <Link
                        to="/contact"
                        data-testid="coup-de-coeur-cta"
                        className="group inline-flex items-center gap-3 bg-lpl-primary text-lpl-text px-7 py-4 rounded-full text-sm uppercase tracking-[0.16em] font-semibold hover:bg-lpl-text hover:text-lpl-bg transition-colors duration-300"
                    >
                        L'essayer en boutique
                        <ArrowUpRight
                            size={18}
                            className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform"
                        />
                    </Link>
                </motion.div>
            </div>
        </section>
    );
}
